/* David Pladen — product page (templates/product.json)
 *
 * - <dp-product>: variant picker (any option name), gallery, stock + ship
 *   date, add to cart through window.DPCart (assets/dp-cart-drawer.js),
 *   sticky bar. Every change fires `dp:variant:change` on document so the
 *   other product sections (specs, box, notes) can follow.
 * - [data-dp-carousel]: arrow buttons + progress bar for a scroll track.
 * - [data-dp-tabs]: accessible tabs (specifications).
 * - [data-dp-card]: colour dots on product cards.
 * - [data-dp-recs-url]: Shopify recommendations loaded after the page.
 * - [data-dp-reviews]: "Show more" and the photo lightbox (.okx).
 * Markup always comes from Liquid; this file only switches states.
 */

(() => {
  if (window.DpProductLoaded) return;
  window.DpProductLoaded = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => (reduceMotion() ? 'auto' : 'smooth');

  /* ---- Sticky offset: header height, measured ---- */
  const setTopOffset = () => {
    const header = document.querySelector('.section-dp-header');
    const height = header ? header.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--pdTop', `${Math.round(height) + 2}px`);
  };
  setTopOffset();
  window.addEventListener('resize', setTopOffset, { passive: true });
  document.addEventListener('DOMContentLoaded', setTopOffset);

  /* ---- Working-day arithmetic for "Ships by" ---- */
  const isoDay = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const addWorkingDays = (from, days, holidays) => {
    const date = new Date(from.getTime());
    let left = days;
    const skip = (d) => d.getDay() === 0 || d.getDay() === 6 || holidays.includes(isoDay(d));
    if (left === 0) {
      while (skip(date)) date.setDate(date.getDate() + 1);
      return date;
    }
    while (left > 0) {
      date.setDate(date.getDate() + 1);
      if (!skip(date)) left -= 1;
    }
    return date;
  };

  /* ================================================================
     <dp-product>
     ================================================================ */
  class DpProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-dp-product-json]');
      this.data = json ? JSON.parse(json.textContent) : { variants: [] };
      this.variantInput = this.querySelector('[data-dp-variant-input]');
      const currentId = this.variantInput ? parseInt(this.variantInput.value, 10) : null;
      this.variant = this.data.variants.find((v) => v.id === currentId) || this.data.variants[0];

      this.bindPicker();
      this.bindGallery();
      this.bindForm();
      this.bindBar();
      this.renderShipDate();
      this.markAvailability();
    }

    /* ---- Variant picker ---- */
    bindPicker() {
      this.picker = this.querySelector('[data-dp-picker]');
      if (!this.picker) return;
      this.picker.addEventListener('click', (event) => {
        const button = event.target.closest('[role="radio"]');
        if (!button) return;
        this.select(button);
      });
      this.picker.addEventListener('keydown', (event) => {
        const button = event.target.closest('[role="radio"]');
        if (!button) return;
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const group = Array.from(button.parentElement.querySelectorAll('[role="radio"]'));
        const next = group[(group.indexOf(button) + keys[event.key] + group.length) % group.length];
        next.focus();
        this.select(next);
      });
    }

    select(button) {
      const group = button.parentElement;
      group.querySelectorAll('[role="radio"]').forEach((b) => {
        const on = b === button;
        b.setAttribute('aria-checked', on);
        b.tabIndex = on ? 0 : -1;
      });
      const selected = Array.from(this.picker.querySelectorAll('[data-dp-option]')).map(
        (opt) => opt.querySelector('[aria-checked="true"]')?.dataset.value
      );
      const match =
        this.data.variants.find((v) => v.options.every((o, i) => o === selected[i])) ||
        this.data.variants.find((v) => v.options[this.optionIndex(button)] === button.dataset.value && v.available) ||
        this.data.variants.find((v) => v.options[this.optionIndex(button)] === button.dataset.value);
      if (match) this.setVariant(match);
    }

    optionIndex(button) {
      return parseInt(button.closest('[data-dp-option]').dataset.dpOption, 10);
    }

    markAvailability() {
      if (!this.picker) return;
      const current = this.variant.options;
      this.picker.querySelectorAll('[data-dp-option]').forEach((opt) => {
        const index = parseInt(opt.dataset.dpOption, 10);
        opt.querySelectorAll('[role="radio"]').forEach((b) => {
          const available = this.data.variants.some(
            (v) => v.available && v.options[index] === b.dataset.value && v.options.every((o, i) => i === index || o === current[i])
          );
          b.classList.toggle('is-unavailable', !available);
        });
      });
    }

    setVariant(variant) {
      this.variant = variant;

      // Picker state (a fallback match may change other options)
      if (this.picker) {
        this.picker.querySelectorAll('[data-dp-option]').forEach((opt) => {
          const index = parseInt(opt.dataset.dpOption, 10);
          opt.querySelectorAll('[role="radio"]').forEach((b) => {
            const on = b.dataset.value === variant.options[index];
            b.setAttribute('aria-checked', on);
            b.tabIndex = on ? 0 : -1;
          });
          const label = opt.querySelector('[data-dp-option-label]');
          const checked = opt.querySelector('[aria-checked="true"]');
          if (label && checked) label.textContent = checked.getAttribute('aria-label');
        });
      }
      this.markAvailability();

      if (this.variantInput) this.variantInput.value = variant.id;
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      // Price
      const price = this.querySelector('[data-dp-price]');
      if (price) price.textContent = variant.price;
      const compare = this.querySelector('[data-dp-compare]');
      if (compare) {
        compare.textContent = variant.compare || '';
        compare.hidden = !variant.compare;
      }

      this.updateGallery();
      this.updateStock();
      this.updateButtons();
      this.updateBar();

      document.dispatchEvent(
        new CustomEvent('dp:variant:change', { detail: { variant, productId: this.data.id, sectionId: this.dataset.sectionId } })
      );
    }

    /* ---- Gallery ---- */
    bindGallery() {
      this.slides = this.querySelector('[data-dp-slides]');
      this.thumbs = this.querySelector('.pd-th');
      if (!this.slides) return;
      this.thumbs?.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-media-id]');
        if (!button) return;
        const slide = this.slides.querySelector(`.pd-sl[data-media-id="${button.dataset.mediaId}"]`);
        if (slide) this.slides.scrollTo({ left: slide.offsetLeft - this.slides.offsetLeft, behavior: scrollBehavior() });
        this.markThumb(button.dataset.mediaId);
      });
      this.slides.addEventListener(
        'scroll',
        () => {
          window.clearTimeout(this.scrollTimer);
          this.scrollTimer = window.setTimeout(() => this.syncThumb(), 60);
        },
        { passive: true }
      );
      this.syncThumb();
    }

    visibleSlides() {
      return Array.from(this.slides.querySelectorAll('.pd-sl:not([hidden])'));
    }

    syncThumb() {
      if (!this.slides || !this.slides.clientWidth) return;
      const slides = this.visibleSlides();
      const index = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
      const slide = slides[Math.min(index, slides.length - 1)];
      if (slide) this.markThumb(slide.dataset.mediaId);
    }

    markThumb(mediaId) {
      this.thumbs?.querySelectorAll('button[data-media-id]').forEach((b) => {
        b.setAttribute('aria-current', b.dataset.mediaId === mediaId);
      });
    }

    updateGallery() {
      if (!this.slides) return;
      const key = `,${this.variant.id},`;
      const hideOthers = this.dataset.hideOtherMedia === 'true';
      [this.slides, this.thumbs].forEach((list) => {
        if (!list) return;
        const items = Array.from(list.querySelectorAll(':scope > [data-media-id]'));
        const own = items.filter((el) => el.dataset.variants.includes(key));
        const rest = items.filter((el) => !el.dataset.variants.includes(key));
        [...own, ...rest].forEach((el) => {
          el.hidden = hideOthers && el.dataset.variants !== '' && !el.dataset.variants.includes(key);
          list.appendChild(el);
        });
      });
      // Pause videos that were moved or hidden
      this.slides.querySelectorAll('video').forEach((video) => video.pause());
      this.slides.scrollTo({ left: 0, behavior: 'auto' });
      const first = this.visibleSlides()[0];
      if (first) {
        const img = first.querySelector('img:not(.bl)');
        if (img) img.loading = 'eager';
        this.markThumb(first.dataset.mediaId);
      }
    }

    /* ---- Stock + ship date ---- */
    updateStock() {
      const box = this.querySelector('[data-dp-stock]');
      if (!box) return;
      const v = this.variant;
      const threshold = parseInt(box.dataset.threshold, 10) || 10;
      const low = v.tracked && v.qty > 0 && v.qty <= threshold;
      const lowEl = box.querySelector('[data-dp-stock-low]');
      const inEl = box.querySelector('[data-dp-stock-in]');
      if (lowEl) {
        lowEl.hidden = !low;
        const fill = lowEl.querySelector('.bar i');
        if (fill && low) {
          fill.style.width = `${Math.round((v.qty / threshold) * 100)}%`;
          // Replay the fill animation
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      }
      const alwaysEl = box.querySelector('[data-dp-stock-always]');
      if (alwaysEl) alwaysEl.hidden = low;
      if (inEl) inEl.hidden = low || 'always' in box.dataset || !v.available || !box.dataset.inText;
    }

    renderShipDate() {
      const box = this.querySelector('[data-dp-stock]');
      const ship = box?.querySelector('[data-dp-ship]');
      if (!ship || !box.dataset.shipText) return;
      const holidays = (box.dataset.holidays || '').split(',').filter(Boolean);
      const max = parseInt(box.dataset.shipMax, 10) || 0;
      const date = addWorkingDays(new Date(), max, holidays);
      let label;
      try {
        label = date.toLocaleDateString(box.dataset.locale || document.documentElement.lang || 'en', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
      } catch (error) {
        label = date.toDateString();
      }
      ship.querySelector('[data-dp-ship-text]').textContent = box.dataset.shipText.replace('[date]', label);
      ship.hidden = false;
    }

    /* ---- Add to cart ---- */
    bindForm() {
      this.form = this.querySelector('[data-dp-form]');
      this.atc = this.querySelector('[data-dp-atc]');
      this.error = this.querySelector('[data-dp-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => {
        if (!window.DPCart) return; // no cart script: normal form post to /cart/add
        event.preventDefault();
        this.add(this.atc);
      });
    }

    async add(trigger) {
      if (!this.variant.available || this.busy) return;
      this.busy = true;
      if (this.error) this.error.hidden = true;
      [this.atc, this.barButton].forEach((b) => b?.setAttribute('aria-busy', 'true'));
      try {
        const result = await window.DPCart.add([{ id: this.variant.id, quantity: 1 }], trigger);
        if (!result || result.status) throw new Error(result?.description || '');
      } catch (error) {
        if (this.error) {
          this.error.textContent = error.message || this.error.dataset.message || window.cartStrings?.error || '';
          this.error.hidden = false;
        }
      } finally {
        this.busy = false;
        [this.atc, this.barButton].forEach((b) => b?.removeAttribute('aria-busy'));
      }
    }

    updateButtons() {
      const available = this.variant.available;
      if (this.atc) {
        this.atc.disabled = !available;
        const label = this.atc.querySelector('[data-dp-atc-label]');
        if (label) label.textContent = available ? this.atc.dataset.label : this.atc.dataset.soldOut;
      }
      if (this.error) this.error.hidden = true;
    }

    /* ---- Sticky bar ---- */
    bindBar() {
      this.bar = this.querySelector('[data-dp-bar]');
      this.barButton = this.bar?.querySelector('[data-dp-bar-add]');
      if (!this.bar || !this.atc) return;
      this.barButton.addEventListener('click', () => this.add(this.barButton));
      // Keep a reference: an unreferenced observer can be garbage-collected
      this.barObserver = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.bar.classList.toggle('on', show);
        this.bar.setAttribute('aria-hidden', !show);
        this.barButton.tabIndex = show ? 0 : -1;
      });
      this.barObserver.observe(this.atc);
    }

    updateBar() {
      if (!this.bar) return;
      const v = this.variant;
      const meta = this.bar.querySelector('[data-dp-bar-meta]');
      if (meta) meta.textContent = `${v.label} · ${v.price}`;
      const img = this.bar.querySelector('.t img');
      if (img && v.thumb) {
        img.removeAttribute('srcset');
        img.src = v.thumb;
      }
      if (this.barButton) {
        this.barButton.disabled = !v.available;
        this.barButton.textContent = v.available ? this.barButton.dataset.label : this.barButton.dataset.soldOut;
      }
    }
  }

  if (!customElements.get('dp-product')) customElements.define('dp-product', DpProduct);

  /* ---- Other sections follow the selected variant ---- */
  document.addEventListener('dp:variant:change', (event) => {
    const { variant } = event.detail;
    document.querySelectorAll('[data-dp-variant-label]').forEach((el) => {
      el.textContent = variant.label;
    });
    document.querySelectorAll('[data-dp-variant-map]').forEach((el) => {
      try {
        const map = JSON.parse(el.dataset.dpVariantMap);
        if (map[variant.id] !== undefined) el.textContent = map[variant.id];
      } catch (error) {
        /* ignore malformed map */
      }
    });
    const swap = (wrap, src) => {
      const img = wrap.querySelector('img');
      if (!img || !src) return;
      img.removeAttribute('srcset');
      img.src = src;
    };
    document.querySelectorAll('[data-dp-variant-img="image"]').forEach((wrap) => swap(wrap, variant.image));
    document.querySelectorAll('[data-dp-variant-img="wrist"]').forEach((wrap) => swap(wrap, variant.wrist));
    document.querySelectorAll('[data-dp-variant-collage]').forEach((wrap) => {
      wrap.querySelectorAll('img').forEach((img) => {
        if (!variant.image) return;
        img.removeAttribute('srcset');
        img.src = variant.image;
      });
    });
  });

  /* ================================================================
     Delegated behaviour (works for sections added in the editor too)
     ================================================================ */

  /* Carousel: arrows + progress bar */
  const updateCarousel = (root) => {
    const track = root.querySelector('[data-track]');
    if (!track) return;
    const bar = root.querySelector('[data-bar]');
    const max = track.scrollWidth - track.clientWidth;
    if (bar) {
      bar.style.width = `${Math.min(100, (track.clientWidth / track.scrollWidth) * 100)}%`;
      bar.style.left = `${(track.scrollLeft / track.scrollWidth) * 100}%`;
    }
    const prev = root.querySelector('[data-dir="-1"]');
    const next = root.querySelector('[data-dir="1"]');
    if (prev) prev.disabled = track.scrollLeft < 4;
    if (next) next.disabled = track.scrollLeft >= max - 4;
  };

  const initCarousels = (scope = document) => {
    scope.querySelectorAll('[data-dp-carousel]').forEach((root) => {
      if (root.dataset.bound) return;
      root.dataset.bound = 'true';
      const track = root.querySelector('[data-track]');
      if (!track) return;
      track.addEventListener('scroll', () => updateCarousel(root), { passive: true });
      root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-dir]');
        if (!button) return;
        const card = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        track.scrollBy({ left: parseInt(button.dataset.dir, 10) * (card ? card.offsetWidth + gap : track.clientWidth), behavior: scrollBehavior() });
      });
      updateCarousel(root);
    });
  };
  window.addEventListener('resize', () => document.querySelectorAll('[data-dp-carousel]').forEach(updateCarousel), { passive: true });

  /* Tabs */
  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-dp-tabs] [role="tab"]');
    if (tab) activateTab(tab);
  });
  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest('[data-dp-tabs] [role="tab"]');
    if (!tab) return;
    const tabs = Array.from(tab.parentElement.querySelectorAll('[role="tab"]'));
    let index = tabs.indexOf(tab);
    if (event.key === 'ArrowRight') index += 1;
    else if (event.key === 'ArrowLeft') index -= 1;
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = tabs.length - 1;
    else return;
    event.preventDefault();
    const next = tabs[(index + tabs.length) % tabs.length];
    next.focus();
    activateTab(next);
  });
  function activateTab(tab) {
    const root = tab.closest('[data-dp-tabs]');
    root.querySelectorAll('[role="tab"]').forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on);
      t.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  }

  /* Product cards: colour dots */
  document.addEventListener('click', (event) => {
    const dot = event.target.closest('[data-dp-card] [data-card-variant]');
    if (!dot) return;
    const card = dot.closest('[data-dp-card]');
    if (!card.variants) card.variants = JSON.parse(card.querySelector('script[type="application/json"]').textContent);
    const v = card.variants[parseInt(dot.dataset.cardVariant, 10)];
    if (!v) return;
    card.querySelectorAll('[data-card-variant]').forEach((d) => d.setAttribute('aria-pressed', d === dot));
    const img = card.querySelector('.im .hit img');
    if (img && v.image) {
      img.removeAttribute('srcset');
      img.src = v.image;
    }
    card.querySelectorAll('[data-card-link]').forEach((a) => {
      a.href = v.url;
    });
    const hit = card.querySelector('.hit');
    if (hit) hit.setAttribute('aria-label', `${hit.getAttribute('aria-label').split(',')[0]}, ${v.label}`);
    const label = card.querySelector('[data-card-label]');
    if (label) label.textContent = v.label;
    const price = card.querySelector('[data-card-price]');
    if (price) price.textContent = v.price;
    const add = card.querySelector('[data-card-add]');
    if (add) {
      add.dataset.dpAdd = v.id;
      add.disabled = !v.available;
      add.textContent = v.available ? add.dataset.label : add.dataset.soldOut;
    }
  });

  /* Recommendations */
  const loadRecommendations = (scope = document) => {
    scope.querySelectorAll('[data-dp-recs-url]').forEach(async (box) => {
      if (box.dataset.loading) return;
      box.dataset.loading = 'true';
      try {
        const response = await fetch(box.dataset.dpRecsUrl);
        const html = await response.text();
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-dp-recs]');
        if (next && box.isConnected) box.replaceWith(next);
        else box.remove();
      } catch (error) {
        box.remove();
      }
    });
  };

  /* Rating under the title: same numbers as the reviews section on this page */
  const syncRating = () => {
    const source = document.querySelector('[data-dp-reviews] [data-rv-avg]');
    if (!source) return;
    document.querySelectorAll('.pd-proof').forEach((proof) => {
      proof.querySelectorAll('[data-rv-avg]').forEach((el) => { el.textContent = source.dataset.rvAvg; });
      proof.querySelectorAll('[data-rv-count]').forEach((el) => { el.textContent = source.dataset.rvCount; });
      proof.querySelector('.pd-stars')?.style.setProperty('--pct', `${source.dataset.rvPct}%`);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncRating);
  else syncRating();
  document.addEventListener('shopify:section:load', syncRating);

  /* Rating link: smooth scroll to the reviews */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-dp-scroll-link]');
    if (!link) return;
    const hash = link.getAttribute('href');
    if (!hash || !hash.startsWith('#')) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    window.history.replaceState({}, '', hash);
  });

  /* Reviews: show more */
  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-ok-more]');
    if (!more) return;
    const root = more.closest('[data-dp-reviews]');
    const hidden = Array.from(root.querySelectorAll('[data-ok-review][hidden]'));
    const step = parseInt(more.dataset.step, 10) || 5;
    hidden.slice(0, step).forEach((r, i) => {
      r.hidden = false;
      if (i === 0) {
        r.setAttribute('tabindex', '-1');
        r.focus({ preventScroll: true });
      }
    });
    if (hidden.length <= step) more.hidden = true;
  });

  /* Reviews: sort */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-ok-sort]');
    if (!select) return;
    const root = select.closest('[data-dp-reviews]');
    const list = root.querySelector('.ok-list');
    const reviews = Array.from(list.querySelectorAll('[data-ok-review]'));
    reviews.sort((a, b) => {
      if (select.value === 'photos') return (b.querySelector('[data-okm]') ? 1 : 0) - (a.querySelector('[data-okm]') ? 1 : 0) || a.dataset.index - b.dataset.index;
      if (select.value === 'longest') return b.querySelector(':scope > p').textContent.length - a.querySelector(':scope > p').textContent.length;
      return a.dataset.index - b.dataset.index;
    });
    const step = parseInt(root.querySelector('[data-ok-more]')?.dataset.step || reviews.length, 10);
    reviews.forEach((r, i) => {
      list.appendChild(r);
      r.hidden = i >= step;
    });
    const more = root.querySelector('[data-ok-more]');
    if (more) more.hidden = reviews.length <= step;
  });

  /* Reviews: photo lightbox (.okx) */
  const lightbox = {
    open(root, index, trigger) {
      this.root = root;
      this.box = root.querySelector('[data-okx]');
      if (!this.box) return;
      /* A photo in a review opens only that review's photos (like Okendo); a grid photo opens all */
      const own = trigger?.closest('[data-ok-review]');
      this.items = Array.from((own || root).querySelectorAll('[data-okm]'));
      this.box.classList.toggle('okx--one', this.items.length < 2);
      this.box.classList.toggle('okx--review', !!own);
      index = Math.max(0, this.items.indexOf(trigger));
      this.trigger = trigger;
      const strip = this.box.querySelector('[data-okx-th]');
      strip.innerHTML = '';
      this.items.forEach((item, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.dataset.okxIndex = i;
        b.setAttribute('aria-label', `${item.getAttribute('aria-label') || ''} ${i + 1}`.trim());
        const img = item.querySelector('img').cloneNode();
        img.alt = '';
        b.appendChild(img);
        strip.appendChild(b);
      });
      this.box.hidden = false;
      void this.box.offsetWidth;
      this.box.classList.add('on');
      document.documentElement.classList.add('dp-scroll-lock');
      this.show(index);
      this.box.querySelector('[data-okx-close]').focus();
    },
    show(index) {
      this.index = Math.max(0, Math.min(index, this.items.length - 1));
      const item = this.items[this.index];
      const review = item.closest('[data-ok-review]');
      const img = item.querySelector('img').cloneNode();
      img.loading = 'eager';
      if (item.dataset.full) {
        img.removeAttribute('srcset');
        img.src = item.dataset.full;
      }
      this.box.querySelector('[data-okx-img]').replaceChildren(img);
      const side = this.box.querySelector('[data-okx-side]');
      side.innerHTML = '';
      const template = item.querySelector('template[data-okm-side]');
      if (template) {
        side.appendChild(template.content.cloneNode(true));
      } else if (review) {
        review.querySelectorAll(':scope > .who, :scope > .ok-p, :scope > .pd-stars, :scope > h4, :scope > p').forEach((part) => {
          const copy = part.cloneNode(true);
          if (copy.matches('.ok-p')) copy.removeAttribute('tabindex');
          side.appendChild(copy);
        });
      }
      this.box.querySelectorAll('[data-okx-index]').forEach((b, i) => b.setAttribute('aria-current', i === this.index));
      this.box.querySelector('[data-okx-step="-1"]').disabled = this.index === 0;
      this.box.querySelector('[data-okx-step="1"]').disabled = this.index === this.items.length - 1;
      this.box.querySelector(`[data-okx-index="${this.index}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center' });
    },
    close() {
      if (!this.box) return;
      this.box.classList.remove('on');
      this.box.hidden = true;
      document.documentElement.classList.remove('dp-scroll-lock');
      this.trigger?.focus();
      this.box = null;
    },
  };

  document.addEventListener('click', (event) => {
    const media = event.target.closest('[data-dp-reviews] [data-okm]');
    if (media) {
      const root = media.closest('[data-dp-reviews]');
      lightbox.open(root, Array.from(root.querySelectorAll('[data-okm]')).indexOf(media), media);
      return;
    }
    if (!lightbox.box) return;
    if (event.target.closest('[data-okx-close]') || event.target === lightbox.box) lightbox.close();
    const step = event.target.closest('[data-okx-step]');
    if (step) lightbox.show(lightbox.index + parseInt(step.dataset.okxStep, 10));
    const thumb = event.target.closest('[data-okx-index]');
    if (thumb) lightbox.show(parseInt(thumb.dataset.okxIndex, 10));
  });

  document.addEventListener('keydown', (event) => {
    if (!lightbox.box) return;
    if (event.key === 'Escape') lightbox.close();
    else if (event.key === 'ArrowLeft') lightbox.show(lightbox.index - 1);
    else if (event.key === 'ArrowRight') lightbox.show(lightbox.index + 1);
    else if (event.key === 'Tab') {
      const focusable = Array.from(lightbox.box.querySelectorAll('button:not([disabled])'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  /* Boot + theme editor */
  const boot = (scope) => {
    initCarousels(scope);
    loadRecommendations(scope);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  document.addEventListener('shopify:section:load', (event) => {
    setTopOffset();
    boot(event.target);
  });
})();